import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Button, ButtonContainer, Container, Divider, DGSelectEdit, DGTextFieldEdit, EditableDataGrid, GridRow, GridItem, MenuItem, Icon, Link, MenuDivider, Stack, Spacer, Typography } from '@emburse/embark-core';
import { AddIcon } from '@emburse/embark-icons';
import { DeleteOutline } from '@mui/icons-material';
import { getExpTypes } from '../../../../api/api';
import UploadDownload from '../../../../views/QuickSetup/components/UploadDownload';
import DataGridLoading from '../../../../views/QuickSetup/components/DataGridLoading';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
import { generateRandomGUID } from '../../../../util/helpers';
import { defaultValidation } from '../../../../util/formValidation';
const ExpenseCategories = forwardRef(({ data, loading, upload, download, alert, currencies }, ref) => {
    useTranslation();
    const [expTypes, setExpTypes] = useState([]);
    const [tableData, setTableData] = useState([]);
    const rowValidation = (row) => {
        const nameInvalid = defaultValidation(row.Name);
        const name = nameInvalid.length && { field: 'Name', reason: nameInvalid };
        return [name];
    };
    useImperativeHandle(ref, () => ({
        getData() {
            return JSON.stringify(tableData);
        },
        isValid() {
            const mappedTable = tableData.map((item, index) => {
                const filteredValidation = rowValidation(item).filter((cell) => !!cell);
                const invalid = filteredValidation.length > 0;
                return (invalid && {
                    row: index + 1,
                    cell: filteredValidation
                });
            });
            return mappedTable.filter((item) => !!item);
        }
    }));
    useEffect(() => {
        if (!expTypes.length) {
            getExpTypes().then(setExpTypes);
        }
        if (!tableData.length && expTypes.length) {
            setTableData(data || []);
        }
    }, [expTypes, tableData.length, data]);
    const addRow = () => setTableData((prevState) => [
        {
            id: generateRandomGUID(),
            Name: '',
            GLCode: '',
            MaxAmount: null,
            MaxAmountCurrencyType: '',
            ExpType: '',
            Dept: ''
        },
        ...prevState
    ]);
    const rowChangeHandler = (row) => setTableData((prevState) => prevState.map((tRow) => (tRow.id === row.id ? row : tRow)));
    const deleteRow = (id) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        const tableRow = tableDataCopy.findIndex((row) => row.id === id);
        tableDataCopy.splice(tableRow, 1);
        setTableData(tableDataCopy);
    };
    const moreActionsMenu = (params) => ({
        sections: [
            {
                items: [
                    {
                        iconText: 'Delete',
                        closeOnClick: true,
                        icon: _jsx(DeleteOutline, {}),
                        onClick: () => deleteRow(params.row.id)
                    }
                ]
            }
        ]
    });
    const saveRow = async (id, row) => {
        const invalid = rowValidation(row).every((item) => !!item);
        // TEditableDGError
        if (invalid) {
            return {
                success: false,
                error: rowValidation(row)
            };
        }
        rowChangeHandler(row);
        return { success: true };
    };
    const preProcessCells = async (params) => {
        if (!params.hasChanged) {
            return { ...params.props };
        }
        const invalid = defaultValidation(params.props.value);
        return { ...params.props, error: invalid.length ? invalid : false };
    };
    return (_jsxs(Container, { disableGutters: true, children: [_jsx(Typography, { variant: "h4", children: $t('Expense Categories') }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`All expenses in Certify must be assigned an expense category. Categories allow users to organize 
                and identify their expenses, and allow Administrators to easily report on the company's area of expenditure. 
                Customers often create categories for Meals, Lodging/Hotels, Airfare, and Mileage.`) }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`Each expense in Certify is assigned an expense type, which dictates the fields available for selection 
                when submitting the expense. Administrators can also drill down into spend by a given expense type. 
                Certify offers the following eight expense types:`) }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Cash Expense (Miscellaneous)')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Hourly Rate')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Lodging')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Meals')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Mileage')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Per Diem')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Rental')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", $t(' Travel')] }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t('We recommend keeping category names to less than 30 characters for optimal mobile viewing.') }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`Certify also allows you to define whether members of a department can or cannot select a certain category 
                - this is referenced by the Filtering Department column, and will be set up on the following page.`) }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('Read more about expense categories '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15460986718221-View-and-Edit-Categories", target: "_blank", children: $t('here') }), $t(' and expense types '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15461047287821-Expense-Types-in-Certify", target: "_blank", children: $t('here') }), "."] }), _jsx(Spacer, { size: 35 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), _jsx(Spacer, { size: 35 }), _jsxs(GridRow, { justifyContent: "space-between", children: [_jsx(GridItem, { children: _jsx(Typography, { variant: "h5", children: $t('Expense Categories') }) }), _jsx(GridItem, { children: _jsx(UploadDownload, { upload: upload, download: download }) })] }), _jsx(Spacer, { size: 35 }), alert.show && (_jsx(Alert, { alertTitle: alert.title, severity: alert.severity, action: _jsx(Button, { color: alert.severity, onClick: alert.onClick, children: $t('See All Errors') }), children: alert.text })), !!tableData.length && (_jsx(ButtonContainer, { alignButtons: "right", sx: { position: 'relative' }, children: _jsx(Button, { variant: "text", size: "small", onClick: addRow, endIcon: _jsx(Icon, { iconUrl: AddIcon }), style: {
                        position: 'absolute',
                        zIndex: 99,
                        right: 0,
                        top: '12px'
                    }, children: $t('Add Row') }) })), _jsx(EditableDataGrid, { autoHeight: true, pageSizeOptions: [5, 10, 100], rows: tableData, hideOnSaveSuccessSnackbar: true, generateMoreActionsMenu: moreActionsMenu, onSave: saveRow, localeText: { noRowsLabel: '' }, columns: [
                    {
                        field: 'Name',
                        headerName: $t('Category Name*'),
                        editable: true,
                        type: 'string',
                        width: 225,
                        preProcessEditCellProps: preProcessCells
                    },
                    {
                        field: 'GLCode',
                        headerName: $t('GL Code'),
                        flex: 2,
                        editable: true,
                        type: 'string'
                    },
                    {
                        field: 'MaxAmount',
                        headerName: $t('Max Amount'),
                        flex: 2,
                        editable: true,
                        type: 'number',
                        valueFormatter: (params) => params.value ? parseFloat(params.value).toFixed(2) : params.value,
                        renderEditCell: (params) => (_jsx(DGTextFieldEdit, { params: params, componentProps: {
                                id: 'MaxAmount',
                                defaultValue: params.row.MaxAmount,
                                inputProps: {
                                    min: '0',
                                    type: 'number'
                                }
                            } }))
                    },
                    {
                        field: 'MaxAmountCurrencyType',
                        headerName: $t('Currency'),
                        flex: 2,
                        editable: true,
                        type: 'string',
                        valueFormatter: (params) => currencies.filter((item) => parseInt(params.value, 10) === item.PK)[0]?.Suffix,
                        renderEditCell: (params) => (_jsx(DGSelectEdit, { params: params, componentProps: {
                                id: 'CurrencyType',
                                defaultValue: params.row,
                                children: currencies.map((item) => {
                                    if (!item?.PK) {
                                        return _jsx(MenuDivider, {}, item.PK);
                                    }
                                    return (_jsx(MenuItem, { value: item.Suffix, children: item.Suffix }, item.PK));
                                })
                            } }))
                    },
                    {
                        field: 'ExpType',
                        headerName: $t('Expense Type'),
                        flex: 2,
                        editable: true,
                        type: 'string',
                        valueFormatter: (params) => expTypes.filter((item) => parseInt(params.value, 10) === item.PK)[0]?.Name,
                        renderEditCell: (params) => (_jsx(DGSelectEdit, { params: params, componentProps: {
                                id: 'ExpType',
                                defaultValue: params.row,
                                children: expTypes.map((item) => (_jsx(MenuItem, { value: item.Name, children: item.Name }, item.PK)))
                            } }))
                    },
                    {
                        field: 'Dept',
                        headerName: $t('Department Filter'),
                        flex: 2,
                        editable: false,
                        type: 'string'
                    }
                ] }), !tableData.length && !loading && (_jsxs(Stack, { spacing: 2, direction: "column", justifyContent: "space-between", sx: {
                    marginTop: '-72px',
                    zIndex: 99,
                    position: 'relative'
                }, children: [_jsx(Button, { size: "medium", onClick: addRow, children: $t('Enter Data Manually') }), _jsx(Typography, { variant: "body2", children: $t('You will need to add ALL data manually') })] })), loading && _jsx(DataGridLoading, {}), _jsx(Spacer, { size: 35 }), _jsx(Typography, { variant: "body2", children: $t('We created some base categories for you, add as many as you need.') })] }));
});
export default ExpenseCategories;
